import { AsnSaveFormData } from '../../Components/ASN/schemas/asnConfiguration.schema';
import Api from '../api';

const ASN = '/asn';
export interface UnitApiZpl {
  zpl: string[];
  request_id: string;
}

export interface HangtagApiZpl {
  zpl: string[];
  request_id: string;
}

export interface CartonApiZpl {
  zpl: string[];
  request_id: string;
}

export const enum PrintRequestStatus {
  REQUESTED = 'REQUESTED',
  CANCELLED = 'CANCELLED',
  ZPL_GENERATED = 'ZPL_GENERATED',
  SENDING_TO_PRINTER = 'SENDING_TO_PRINTER',
  SENT_TO_PRINTER_OK = 'SENT_TO_PRINTER_OK',
  SENT_TO_PRINTER_FAIL = 'SENT_TO_PRINTER_FAIL',
}

export interface CancelPrintRequest {
  request_id: string;
  request_status: PrintRequestStatus;
}

export interface PrintRequest {
  request_id: string;
  request_status: PrintRequestStatus;
  po_internal_line_id: string;
  po_internal_id: string;
  print_request_user: string;
  sku: string;
  label_type: 'unit' | 'hangtag';
  number_of_labels: number;
  print_request_timestamp: string;
  purchase_order: {
    po_number: string;
  };
  error?: string;
}

interface PrintedSamples {
  unit: boolean;
  carton: boolean;
  hangtag: boolean;
}

export interface PrintingOnboardingResponse {
  printed_samples: PrintedSamples;
}

export interface PrintingOnboarding {
  id: string;
  vendor_id: string;
  printed_samples: {
    unit: boolean;
    hangtag: boolean;
    carton: boolean;
  };
  approved_by: string;
  approved_at: string;
  rejected_at: string;
  created_at: string;
  updated_at: string;
  pending_approval_reason: string;
}

export type PrintingPermissionStatus =
  | 'approved'
  | 'pending_approval'
  | 'disabled';

export type PrintingLabelType =
  | 'unit_label'
  | 'hangtag'
  | 'carton_iccl'
  | 'carton_asn';

export interface PrintingPermission {
  status: PrintingPermissionStatus;
  pending_approval_reason: string | null;
  approved_at: string | null;
  approved_by?: string | null;
}

export interface PrintingPermissionRaw {
  label_type: PrintingLabelType;
  status: PrintingPermissionStatus;
  pending_approval_reason: string | null;
  approved_at: string | null;
  approved_by?: string | null;
}

export interface PrintingPermissions {
  unit_label: PrintingPermission;
  hangtag: PrintingPermission;
  carton_iccl: PrintingPermission;
  carton_asn: PrintingPermission;
}

const DEFAULT_PERMISSION: PrintingPermission = {
  status: 'disabled',
  pending_approval_reason: null,
  approved_at: null,
  approved_by: null,
};

export type CartonSizeType =
  | 'Standard1'
  | 'Standard2'
  | 'Standard3'
  | 'Standard4'
  | 'Custom';

export interface AsnSkuConfiguration {
  id: string;
  asn_number: string;
  asn_header_id: string;
  po_number: string;
  po_line_item_id?: string | null;
  wms_sku_id: string;
  actual_quantity?: number | null;
  items_per_carton?: number | null;
  carton_size?: CartonSizeType | null;
  carton_weight?: number | null;
  carton_length?: number | null;
  carton_width?: number | null;
  carton_height?: number | null;
  tare_weight?: number | null;
  created_at: string;
  updated_at: string;
}

export interface AsnHeader {
  id: string;
  po_number: string;
  po_type?: string | null;
  po_country_of_origin?: string | null;
  po_due_date?: string | null;
  po_shipment_mode?: string | null;
  po_incoterm?: string | null;
  vendor_name?: string | null;
  vendor_id: string;
  asn_number: string;
  destination_country?: string | null;
  final_destination_location?: string | null;
  port_of_loading?: string | null;
  port_of_entry?: string | null;
  shipment_type?: 'LTL' | 'FTL' | 'LCL' | 'FCL' | null;
  trailer_number?: string | null;
  bol_number?: string | null;
  carrier_name?: string | null;
  carrier_scac?: string | null;
  seal_number?: string | null;
  ship_date?: string | null;
  carton_count?: number | null;
  unit_count?: number | null;
  allow_mixed_cartons?: boolean | null;
  last_configured_at?: string | null;
  packed_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AsnResponse extends AsnHeader {
  sku_configurations: AsnSkuConfiguration[];
}

function findPermission(
  permissionsArray: PrintingPermissionRaw[] | undefined | null,
  labelType: PrintingLabelType,
): PrintingPermission {
  const found = permissionsArray?.find((p) => p.label_type === labelType);
  if (!found) return DEFAULT_PERMISSION;

  return {
    status: found.status,
    pending_approval_reason: found.pending_approval_reason,
    approved_at: found.approved_at,
    approved_by: found.approved_by,
  };
}

export function transformPrintingPermissions(
  permissionsArray: PrintingPermissionRaw[] | undefined | null,
): PrintingPermissions {
  return {
    unit_label: findPermission(permissionsArray, 'unit_label'),
    hangtag: findPermission(permissionsArray, 'hangtag'),
    carton_iccl: findPermission(permissionsArray, 'carton_iccl'),
    carton_asn: findPermission(permissionsArray, 'carton_asn'),
  };
}

const AsnAPI = {
  generateAsn(userId: string, poNumber: string): Promise<AsnResponse> {
    return Api.post(`/users/${userId}${ASN}`, {
      data: { po_number: poNumber },
    });
  },

  getAsnByPoNumber(userId: string, poNumber: string): Promise<AsnResponse> {
    return Api.get(`/users/${userId}${ASN}/${poNumber}`);
  },

  saveAsnConfiguration(
    userId: string,
    asnNumber: string,
    data: AsnSaveFormData,
  ): Promise<void> {
    return Api.patch(`/users/${userId}${ASN}/${asnNumber}/save`, {
      data,
    });
  },

  getPoUnitLabelData(
    userId: string,
    poId: string,
    extraLabel: number,
  ): Promise<UnitApiZpl[]> {
    return Api.post(`/users/${userId}${ASN}/unit/${poId}`, {
      params: { extraLabelsPercent: extraLabel / 100 },
    });
  },

  getPoLineUnitLabelData(
    userId: string,
    lineId: string,
    numLabels: number | undefined,
  ): Promise<UnitApiZpl> {
    return Api.post(`/users/${userId}${ASN}/unit/line/${lineId}`, {
      params: { labelsQty: numLabels },
    });
  },

  createTestPrintLabelRequest(
    userId: string,
    type: 'unit' | 'hangtag' | 'carton',
  ): Promise<UnitApiZpl> {
    return Api.post(`/users/${userId}${ASN}/test-print?type=${type}`);
  },

  getPoLabelHangtagData(userId: string, poId: string, extraLabel: number) {
    return Api.get(`/users/${userId}${ASN}/hangtag/${poId}`, {
      params: { extraLabelsPercent: extraLabel / 100 },
    });
  },

  getPoLineLabelHangtagData(
    userId: string,
    lineId: string,
    numLabels: number | undefined,
  ) {
    return Api.get(`/users/${userId}${ASN}/hangtag/line/${lineId}`, {
      params: { labelsQty: numLabels },
    });
  },

  getPoLineLabelCartonData(
    userId: string,
    lineId: string,
    numLabels: number | undefined,
    mix: boolean,
    unitCount: number,
  ) {
    return Api.get(`/users/${userId}${ASN}/carton/line/${lineId}`, {
      params: { labelsQty: numLabels, mix, unitCount },
    });
  },

  updatePrintRequestStatus(
    userId: string,
    requestId: string,
    status: PrintRequestStatus,
    error?: string,
  ): Promise<PrintRequest> {
    return Api.put(`/users/${userId}${ASN}/requests/${requestId}`, {
      data: { request_status: status, error },
    });
  },

  getPrintingOnboardingTestStatus(
    userId: number,
  ): Promise<PrintingOnboardingResponse> {
    return Api.get(`/users/${userId}${ASN}/printing-onboarding`);
  },

  upsertPrintingOnboardingTestLabels(
    userId: number,
    data: { printed_samples: PrintedSamples },
  ): Promise<void> {
    return Api.post(`/users/${userId}${ASN}/printing-onboarding`, {
      data,
    });
  },

  getPrintRequests(userId: string, active?: boolean) {
    return Api.get(
      `/users/${userId}${ASN}/requests?type=${active ? 'active' : 'all'}`,
    );
  },

  cancelPrintRequests(userId: string, requestIds: string[]) {
    fetch(
      `${process.env.REACT_APP_API_TARGET}/users/${userId}/asn/requests/cancel`,
      {
        keepalive: true,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Auth-Token': `${localStorage.getItem('fashionnova_token_storage')}`,
        },
        body: JSON.stringify({
          request_ids: requestIds,
        }),
      },
    );
  },
};

export default AsnAPI;
